import {useMemo} from 'react';
import {Box, Flex, HStack, Icon, Text, VStack} from '@chakra-ui/react';
import {ChevronDownIcon, ChevronUpIcon} from '@chakra-ui/icons';
import {TriSelection} from 'api-types/ffconnect';
import {LigneDeDevisDesc, LigneDePanierDesc} from 'api-types/ffconnect-hal';
import {State} from '@synako/halitrek';
import {UseHalStatesHook, useHalStates} from '@synako/haligator';
import {trierEtFiltrerCategories, usePanier} from 'ff-common';
import {DevisLineItemClient} from './DevisLineItemClient';
import {useDisclosure} from '@chakra-ui/react';

// Composant pour l'en-tête d'une catégorie
const CategorieHeader: React.FC<{
  libelle: string;
  count: number;
  isOpen: boolean;
  onToggle: () => void;
}> = ({libelle, count, isOpen, onToggle}) => (
  <Flex
    as='button'
    onClick={onToggle}
    alignItems='center'
    justifyContent='start'
    width='100%'
    p={3}
    pl={0}
    borderRadius='md'
    _hover={{bg: 'gray.bg'}}
    mb={2}
  >
    <HStack>
      <Text
        fontWeight='bold'
        fontSize='md'
      >
        {libelle}
      </Text>
      <Box
        textColor='gray.dark'
        fontWeight='600'
        fontSize='xs'
        bg='gray.light'
        p='1'
        borderRadius='8px'
        minW='2em'
        textAlign='center'
        ml={2}
      >
        {count}
      </Box>
    </HStack>
    <Icon
      as={isOpen ? ChevronUpIcon : ChevronDownIcon}
      boxSize='20px'
      ml='auto'
    />
  </Flex>
);

// Composant pour une section de catégorie individuelle
const DevisCategorieSection: React.FC<{
  categorie_id: string;
  libelle_web: string;
  lignesDeDevisStates: State<LigneDeDevisDesc>[];
  lignePanierMap: Map<string, State<LigneDePanierDesc>>;
}> = ({categorie_id, libelle_web, lignesDeDevisStates, lignePanierMap}) => {
  const {isOpen, onToggle} = useDisclosure({defaultIsOpen: true});

  return (
    <Box
      key={categorie_id}
      mb={4}
    >
      <CategorieHeader
        libelle={libelle_web}
        count={lignesDeDevisStates.length}
        isOpen={isOpen}
        onToggle={onToggle}
      />

      {/* Contenu de la catégorie */}
      {isOpen && (
        <VStack
          spacing={0}
          align='stretch'
        >
          {lignesDeDevisStates.map(
            (ligneDeDevisState: State<LigneDeDevisDesc>) => (
              <DevisLineItemClient
                key={ligneDeDevisState.data.id}
                ligneDeDevisState={ligneDeDevisState}
                lignePanier={lignePanierMap.get(
                  ligneDeDevisState.data.code_article,
                )}
              />
            ),
          )}
        </VStack>
      )}
    </Box>
  );
};

// Composant principal pour afficher les lignes de devis catégorisées
export const DevisSection: React.FC<{
  lignesDeDevis: UseHalStatesHook<LigneDeDevisDesc>;
  tri_selection_devis?: TriSelection;
}> = ({lignesDeDevis, tri_selection_devis}) => {
  // Récupération des lignes de panier pour optimiser les lookups
  const {states: lignesDePanier} = useHalStates(
    usePanier().followAll('lignes_de_panier'),
  );

  // Créer une map pour lookup rapide des lignePanier par code_article
  const lignePanierMap = useMemo(() => {
    const map = new Map<string, State<LigneDePanierDesc>>();
    if (lignesDePanier) {
      lignesDePanier.forEach((lignePanier) => {
        map.set(lignePanier.data.code_article, lignePanier);
      });
    }
    return map;
  }, [lignesDePanier]);

  const lignesDeDevisStatesMap = useMemo(() => {
    const map = new Map<string, State<LigneDeDevisDesc>>();
    lignesDeDevis.states?.forEach((state) => {
      map.set(state.data.code_article, state);
    });
    return map;
  }, [lignesDeDevis]);

  // Préparation du contenu des catégories
  const categoriesContent = useMemo(() => {
    if (!tri_selection_devis || tri_selection_devis.length === 0) {
      return null;
    }

    return trierEtFiltrerCategories(
      tri_selection_devis,
      lignesDeDevisStatesMap,
    ).map((categoryData) => {
      if (!categoryData) return null;

      const {
        categorie_id,
        libelle_web,
        articles: lignesDeDevisStatesInDevis,
      } = categoryData;

      return (
        <DevisCategorieSection
          key={categorie_id}
          categorie_id={categorie_id}
          libelle_web={libelle_web}
          lignesDeDevisStates={lignesDeDevisStatesInDevis}
          lignePanierMap={lignePanierMap}
        />
      );
    });
  }, [tri_selection_devis, lignesDeDevisStatesMap, lignePanierMap]);

  return (
    <Box p={4}>
      <VStack
        w='100%'
        alignItems='stretch'
        spacing={0}
      >
        {/* Section des produits catégorisés */}
        {categoriesContent && <>{categoriesContent}</>}
      </VStack>
    </Box>
  );
};
